import { useFeatures } from "@/lib/features";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import MonitoringPage from "@/pages/MonitoringPage";
import InvoicesPage from "@/pages/InvoicesPage";
import AlertsPage from "@/pages/AlertsPage";
import AffiliatePage from "@/pages/AffiliatePage";
import SettingsPage from "@/pages/SettingsPage";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPaymentLogs from "@/pages/admin/AdminPaymentLogs";
import RepricingPage from "@/pages/RepricingPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import FaqPage from "@/pages/FaqPage";
import ShopsPage from "@/pages/ShopsPage";
import AdminFaq from "@/pages/admin/AdminFaq";
import ComparePage from "@/pages/ComparePage";
import VsPage from "@/pages/VsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppSumoPage from "@/pages/AppSumoPage";
import AdminAppSumo from "@/pages/admin/AdminAppSumo";
import AdminScripts from "@/pages/admin/AdminScripts";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookieConsent from "@/components/CookieConsent";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-pulse-slow text-lg">Laden...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-pulse-slow text-lg">Laden...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
}

function AffiliateRoute({ children }) {
  const { isEnabled } = useFeatures();
  return isEnabled("affiliate_module") ? children : <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/appsumo" element={<AppSumoPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/vergelijk" element={<ComparePage />} />
      <Route path="/vergelijk/:slug" element={<VsPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="repricing" element={<RepricingPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="affiliate" element={<AffiliateRoute><AffiliatePage /></AffiliateRoute>} />
        <Route path="shops" element={<ShopsPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="email-templates" element={<AdminEmailTemplates />} />
        <Route path="affiliates" element={<AdminAffiliates />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="payment-logs" element={<AdminPaymentLogs />} />
        <Route path="faq" element={<AdminFaq />} />
        <Route path="appsumo" element={<AdminAppSumo />} />
        <Route path="scripts" element={<AdminScripts />} />
        <Route path="translations" element={<AdminTranslations />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      <Route path="*" element={
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-4" data-testid="not-found-page">
          <p className="text-5xl font-bold text-[#2CA8AC]">404</p>
          <p className="text-muted-foreground">Page not found / Pagina niet gevonden</p>
          <a href="/" className="text-sm font-semibold text-[#2CA8AC] hover:underline">Smartpriced</a>
        </div>
      } />
    </Routes>
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <AppRoutes />
          </ErrorBoundary>
          <CookieConsent />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
